import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { GraniteDataGrid } from '@tecuity/granite-ui/GraniteDataGrid'
import format from 'date-fns/format'
import { IS_USING_LIEN_PARTY_EXTENSIONS } from '../../../requests'
import { useDispatch } from 'react-redux';
import { SET_USING_LIEN_PARTY_EXTENSIONS } from '../../../store'

const HistoricalDetails = () => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const dispatch = useDispatch();
  const {
    ucc = {},
    notaryRecordDetailsSettings: config,
    } = useSelector((state) => state.record, shallowEqual)
  const isUsingLienPartyExtensions = useSelector((state) => state.app.usingLienExtensions, shallowEqual)

  useEffect(() => {
    if(isUsingLienPartyExtensions === null) {
      IS_USING_LIEN_PARTY_EXTENSIONS((result) => {
        dispatch({type: SET_USING_LIEN_PARTY_EXTENSIONS, usingLienExtensions:result});
        },() => {
        dispatch({type: SET_USING_LIEN_PARTY_EXTENSIONS, usingLienExtensions:false});
      });
    }
  });

  const debtorTemplate = useMemo(() => {
    const template = []
    if (config['HistoricalDebtorName'] && config['HistoricalDebtorName'].Enabled)
      template.push({
        field: 'DetailName',
        flex: 1,
        headerName: config['HistoricalDebtorName'].DisplayName,
        disableColumnMenu: true,
      })
    if (config['HistoricalDebtorAddress'] && config['HistoricalDebtorAddress'].Enabled)
      template.push({
        field: 'SingleLineAddress',
        flex: 2,
        headerName: config['HistoricalDebtorAddress'].DisplayName,
        disableColumnMenu: true,
      })
    if (config['HistoricalActionTaken'] && config['HistoricalActionTaken'].Enabled)
      template.push({
        field: 'ActionTaken',
        flex: 1,
        headerName: config['HistoricalActionTaken'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return `${params.row.ActionTaken} Debtor`
        },
      })
    if (config['HistoricalActionDate'] && config['HistoricalActionDate'].Enabled)
      template.push({
        field: 'ActionDate',
        flex: 1,
        headerName: config['HistoricalActionDate'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return format(new Date(params.row.ActionDate || null), 'MM/dd/yyyy h:mm a')
        },
      })
    if (config['HistoricalIsCurrent'] && config['HistoricalIsCurrent'].Enabled)
      template.push({
        field: 'IsCurrent',
        flex: 1,
        headerName: config['HistoricalIsCurrent'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return params.row.IsCurrent ? 'Yes' : 'No'
        },
      })
    return template
  }, [config])

  const securedPartyTemplate = useMemo(() => {
    const template = []
    if (config['HistoricalSecuredPartyName'] && config['HistoricalSecuredPartyName'].Enabled)
      template.push({
        field: 'DetailName',
        flex: 1,
        headerName: config['HistoricalSecuredPartyName'].DisplayName,
        disableColumnMenu: true,
      })
    if (config['HistoricalSecuredPartyAddress'] && config['HistoricalSecuredPartyAddress'].Enabled)
      template.push({
        field: 'SingleLineAddress',
        flex: 2,
        headerName: config['HistoricalSecuredPartyAddress'].DisplayName,
        disableColumnMenu: true,
      })
    if (config['HistoricalActionTaken'] && config['HistoricalActionTaken'].Enabled)
      template.push({
        field: 'ActionTaken',
        flex: 1,
        headerName: config['HistoricalActionTaken'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return `${params.row.ActionTaken} Secured Party`
        },
      })
    if (config['HistoricalActionDate'] && config['HistoricalActionDate'].Enabled)
      template.push({
        field: 'ActionDate',
        flex: 1,
        headerName: config['HistoricalActionDate'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return format(new Date(params.row.ActionDate || null), 'MM/dd/yyyy h:mm a')
        },
      })
    if (config['HistoricalIsCurrent'] && config['HistoricalIsCurrent'].Enabled)
      template.push({
        field: 'IsCurrent',
        flex: 1,
        headerName: config['HistoricalIsCurrent'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return params.row.IsCurrent ? 'Yes' : 'No'
        },
      })
    return template
  }, [config])

  const countyTemplate = useMemo(() => {
    const template = []
    if (config['CountyName'] && config['CountyName'].Enabled)
      template.push({
        field: 'CountyName',
        flex: 1,
        headerName: config['CountyName'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return `${params.row.County.Name} `
        },
      })
    if (config['HistoricalActionTaken'] && config['HistoricalActionTaken'].Enabled)
      template.push({
        field: 'ActionTaken',
        flex: 1,
        headerName: config['HistoricalActionTaken'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return `${params.row.UCCActionType.Name} County`
        },
      })
    if (config['HistoricalActionDate'] && config['HistoricalActionDate'].Enabled)
      template.push({
        field: 'ActionDate',
        flex: 1,
        headerName: config['HistoricalActionDate'].DisplayName,
        disableColumnMenu: true,
        renderCell: (params) => {
          return format(new Date(params.row.ActionDate || null), 'MM/dd/yyyy h:mm a')
        },
      })
    return template
  }, [config])

  const debtorData = useMemo(() => {
    const parties = []
    ;(ucc.HistoricalDebtors || []).forEach((debtor) => {
      parties.push({
        ...debtor,
        ActionTaken: debtor.UCCPartyActionType.Name,
        ActionDate: debtor.CreatedDate,
      })
      if (!isUsingLienPartyExtensions && debtor.DeletedByFiling) {
        parties.push({
          ...debtor,
          UCCDebtorId: debtor.UCCDebtorId + 'delete',
          ActionTaken: 'Delete',
          ActionDate: debtor.DeletedByFiling.ReceivedDate,
        })
      }
    })
    return parties.sort(
      (a, b) => new Date(b.ActionDate).getTime() - new Date(a.ActionDate).getTime()
    )
  }, [ucc, isUsingLienPartyExtensions])

  const securedPartyData = useMemo(() => {
    const parties = []
    ;(ucc.HistoricalSecuredParties || []).forEach((sp) => {
      parties.push({
        ...sp,
        ActionTaken: sp.UCCPartyActionType.Name,
        ActionDate: sp.CreatedDate,
      })
      if (!isUsingLienPartyExtensions && sp.DeletedByFiling) {
        parties.push({
          ...sp,
          UCCSecuredPartyId: sp.UCCSecuredPartyId + 'delete',
          ActionTaken: 'Delete',
          ActionDate: sp.DeletedByFiling.ReceivedDate,
        })
      }
    })
    return parties.sort(
      (a, b) => new Date(b.ActionDate).getTime() - new Date(a.ActionDate).getTime()
    )
  }, [ucc, isUsingLienPartyExtensions])

  const countyData = useMemo(() => {
    const counties = []
    ;(ucc.HistoricalLienCounties || []).forEach((county) => {
      counties.push({
        ...county,
        ActionTaken: county.UCCActionType.Name,
        ActionDate: county.CreatedDate,
      })
    })
    return counties.sort(
      (a, b) => new Date(b.ActionDate) - new Date(a.ActionDate)
    )
  }, [ucc])

  return (
    <div className="party-history">
      <GraniteDataGrid
        getRowId={(row) => row.UCCDebtorId}
        rows={debtorData}
        columns={debtorTemplate}
        pageSize={rowsPerPage}
        onPageSizeChange={(newPageSize) => setRowsPerPage(newPageSize)}
        rowsPerPageOptions={[10, 25, 100]}
      />
      <GraniteDataGrid
        getRowId={(row) => row.UCCSecuredPartyId}
        rows={securedPartyData}
        columns={securedPartyTemplate}
        pageSize={rowsPerPage}
        onPageSizeChange={(newPageSize) => setRowsPerPage(newPageSize)}
        rowsPerPageOptions={[10, 25, 100]}
      />
      {(ucc.HistoricalLienCounties && ucc.HistoricalLienCounties.length > 0) && (
        <GraniteDataGrid
          getRowId={(row) => row.LienCountyId}
          rows={countyData}
          columns={countyTemplate}
          pageSize={rowsPerPage}
          onPageSizeChange={(newPageSize) => setRowsPerPage(newPageSize)}
          rowsPerPageOptions={[10, 25, 100]}
        />
      )}
    </div>
  )
}

export default HistoricalDetails

//   "HistoricalDebtorName": {
//       "CardNumber": 0,
//       "Column": 0,
//       "Enabled": true,
//       "DisplayName": "Debtor Name",
//       "FieldType": 0
//   },
//   "HistoricalDebtorAddress": {
//       "CardNumber": 0,
//       "Column": 0,
//       "Enabled": true,
//       "DisplayName": "Debtor Address",
//       "FieldType": 0
//   },
//   "HistoricalSecuredPartyName": {
//       "CardNumber": 0,
//       "Column": 0,
//       "Enabled": true,
//       "DisplayName": "Secured Party Name",
//       "FieldType": 0
//   },
//   "HistoricalSecuredPartyAddress": {
//       "CardNumber": 0,
//       "Column": 0,
//       "Enabled": true,
//       "DisplayName": "Secured Party Address",
//       "FieldType": 0
//   },
//   "HistoricalActionTaken": {
//       "CardNumber": 0,
//       "Column": 0,
//       "Enabled": true,
//       "DisplayName": "Action Taken",
//       "FieldType": 0
//   },
//   "HistoricalActionDate": {
//       "CardNumber": 0,
//       "Column": 0,
//       "Enabled": true,
//       "DisplayName": "Action Date",
//       "FieldType": 0
//   }
// }
